import React from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Home from "./pages/Home";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import { Amplify } from "aws-amplify";
import amplifyconfig from "./amplifyconfiguration.json";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { MyProvider } from "./context/MyContext";
import { AuthProvider } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import Profile from "./pages/Profile";
import Account from "./pages/Account";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useContextData } from "./hooks/useContextData";
import CssBaseline from "@mui/material/CssBaseline";

Amplify.configure(amplifyconfig);

const buildTheme = (mode: "light" | "dark") =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: "#004152",
        light: "#005a70",
        contrastText: "#ffffff",
      },
      background: {
        default: mode === "dark" ? "#00050F" : "#f8f9fb",
        paper: mode === "dark" ? "#15121F" : "#ffffff",
      },
      text: {
        primary: mode === "dark" ? "#FAFAFA" : "#191c1e",
        secondary: mode === "dark" ? "#B0B8C4" : "#4a5568",
        disabled: mode === "dark" ? "#4a5568" : "#9ba4ae",
      },
      divider:
        mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(25,28,30,0.08)",
    },
    typography: {
      fontFamily: '"Manrope", "Inter", "BlinkMacSystemFont", sans-serif',
      h1: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 700,
      },
      h2: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 700,
      },
      h3: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 600,
      },
      h4: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 600,
      },
      h5: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 600,
      },
      h6: {
        fontFamily: '"Manrope", sans-serif',
        letterSpacing: "-0.02em",
        fontWeight: 600,
      },
      body1: { fontFamily: '"Inter", sans-serif' },
      body2: { fontFamily: '"Inter", sans-serif' },
      caption: { fontFamily: '"Inter", sans-serif' },
      overline: { fontFamily: '"Inter", sans-serif', letterSpacing: "0.1em" },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 600 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
    },
  });

/** Reads from MyContext to provide a mode-aware MUI theme */
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { theme: appTheme } = useContextData();
  const muiTheme = buildTheme(appTheme);
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Authenticator.Provider>
      <AuthProvider>
        <I18nProvider>
          <PayPalScriptProvider
            options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "" }}
          >
            <MyProvider>
              <ThemeWrapper>
                <BrowserRouter>
                  <div className="App">
                    <Routes>
                      {/* Public routes */}
                      <Route path="/about" element={<About />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/login" element={<Login />} />

                      {/* Protected routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Home />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/account"
                        element={
                          <ProtectedRoute>
                            <Account />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </div>
                </BrowserRouter>
              </ThemeWrapper>
            </MyProvider>
          </PayPalScriptProvider>
        </I18nProvider>
      </AuthProvider>
    </Authenticator.Provider>
  );
};

export default App;
